import { toJS } from 'mobx';

import type { Address } from '~/api/DeliveryAddress';
import { DeliveryMethods } from '~/api/ETA';
import { isPickupStoreChanging } from '~/components/DeliveryPickupAddressModal/pickupStoreChange';
import type { PickupPoint } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import type { EditAddressData } from '~/components/ModalDeliveryAddress/interface';
import {
  sendAddressChangedAnalytics,
  sendPostSaveAnalytics,
} from '~/components/ModalDeliveryAddress/saveAddressUtils';
import { deliveryZonesQuery } from '~/components/ModalEditAddressV3/hooks/useDeliveryZones';
import { selectPickupPoints } from '~/hooks/usePickupZones';
import { queryClient } from '~/providers/QueryClientProvider';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { DOMAIN, REASON, reportWarning } from '~/services/telemetry';
import { checkoutStore, mainStore, orderStore, userStore } from '~/stores';
import type { DeliveryAddress } from '~/stores/UserStore';
import { defaultAddressType } from '~/utils/addressName';
import { resolveAddressUsage } from '~/utils/addressUsage';
import { deliveryAddressFromSaved } from '~/utils/checkoutDestination';

import { commitPickupSelection } from './PickupSelection';
import { pickupStoreLinkState } from './pickupStoreLinkState';

/**
 * Query param carrying the warehouse code of the store an in-store QR code
 * points at. Not `store` — that one already redirects mobile visitors to the
 * app store (see hooks/useStoreRedirect.ts).
 */
export const PICKUP_STORE_PARAM = 'pickup-store';

/**
 * A form with nothing filled in — the shape both save helpers expect.
 *
 * Built fresh each time rather than shared: the save helpers hand the object
 * (and its `instructions` array) straight to the store, where MobX makes it
 * observable and later writes mutate it in place. One shared constant would
 * carry those writes into the next scan.
 */
const blankEditAddressData = (): EditAddressData => ({
  addressId: null,
  address2: '',
  addressFloor: '',
  addressBld: '',
  addressApt: '',
  type: 'home',
  comment: '',
  instructions: [],
});

/**
 * Finds the click-and-collect point served by `warehouseCode`.
 *
 * Zones are the source of truth, so opening a new store needs no release: as
 * soon as it has a click-and-collect zone its code resolves here. An unknown
 * or disabled code resolves to null and the caller falls back to the normal
 * address flow. Matched case-insensitively — the code travels through a
 * printed QR code and a URL, where case is not worth losing an order over.
 *
 * Goes through the same react-query entry as useDeliveryZones rather than
 * calling the store directly: the address modal this flow may still fall
 * through to asks for the very same zones, and a link should not cost the
 * customer two identical requests.
 */
export async function resolvePickupStoreByCode(
  warehouseCode: string,
): Promise<PickupPoint | null> {
  const coverages = await queryClient
    .fetchQuery(deliveryZonesQuery())
    .catch(() => null);

  if (!coverages?.length) {
    // requestZones answers a failed request with [], which react-query cannot
    // tell from a tenant that genuinely has no zones — and would then serve
    // for an hour to the address modal this flow falls back to, leaving the
    // customer standing in the store unable to pick it by hand either.
    queryClient.removeQueries({ queryKey: deliveryZonesQuery().queryKey });
    return null;
  }

  const target = warehouseCode.trim().toLowerCase();

  return (
    selectPickupPoints(coverages).find(
      (point) => point.warehouseCode.toLowerCase() === target,
    ) ?? null
  );
}

/**
 * The customer's saved pickup address for this warehouse, if they have one.
 *
 * Without it every scan of the same QR code files another address into their
 * profile, since the save helper adds a new record whenever it is handed an
 * address with no id.
 */
function findSavedPickupAddress(warehouseCode: string): Address | undefined {
  return userStore.addressList.find(
    (address) =>
      resolveAddressUsage(address.usage) === 'pickup' &&
      address.warehouse_code === warehouseCode,
  );
}

/**
 * Rebuilds a saved address into the pair the save helpers take.
 *
 * Both the address and the form fields come from the record itself, never from
 * blanks: the save helper's update branch writes floor, apartment, comment and
 * instructions straight from `editAddressData`, so handing it an empty form
 * would wipe everything the customer had stored on that address — on every
 * single scan. The address half goes through the same mapper checkout uses, so
 * a new field on `Address` cannot reach one path and miss the other.
 */
function fromSavedAddress(address: Address): {
  deliveryAddress: DeliveryAddress;
  editAddressData: EditAddressData;
} {
  return {
    deliveryAddress: deliveryAddressFromSaved(address),
    editAddressData: {
      addressId: address.id,
      address2: address.street_address_2 ?? '',
      addressFloor: address.floor ?? '',
      addressBld: address.building ?? '',
      addressApt: address.apartment ?? '',
      type: address.type || defaultAddressType(),
      comment: address.comment || '',
      instructions: toJS(address.instructions || []),
    },
  };
}

/**
 * Resolves the store's own address by reverse-geocoding its coordinates.
 *
 * Returns null when the point cannot be turned into a usable address. Alerts
 * it pushed on the way out are cleared by the caller, together with those of
 * every other request in the sequence.
 */
async function geocodeStoreAddress(
  point: PickupPoint,
): Promise<DeliveryAddress | null> {
  const address = await userStore
    .geocodingByCoordinates(point.center)
    .catch(() => null);

  return address?.city ? address : null;
}

/**
 * How long the storefront may stay hidden while a link resolves.
 *
 * Without it there is no bound at all: every request in the sequence retries
 * network failures several times behind a 30s timeout, so a weak in-store
 * connection could hold the page blank for minutes. Past the budget the link
 * is abandoned before anything commits and the customer gets the ordinary
 * address flow, which is the same outcome as any other failure.
 */
const LINK_BUDGET_MS = 10_000;

/** Codes already reported, so one dead QR code cannot flood Sentry. */
const reportedFailures = new Set<string>();

/**
 * Selects the store behind an in-store QR code as the customer's pickup
 * address, so scanning it lands them straight in that store's storefront.
 *
 * Deliberately overrides whatever address was chosen before: the customer is
 * standing in this store. Returns false when the link cannot be honoured —
 * unknown code, store outside coverage, geocoding or ETA failure, or simply
 * taking too long — and leaves the session exactly as it found it, so the
 * caller can fall through to the normal address selection.
 */
export async function applyPickupStoreLink(
  warehouseCode: string,
): Promise<boolean> {
  const alertsBefore = new Set(mainStore.alerts);
  const deadline = Date.now() + LINK_BUDGET_MS;
  /**
   * Cancels the requests still in flight when the budget runs out.
   *
   * Losing a `Promise.race` only stops this function waiting — the request
   * itself carries on, and the side-effectful ETA would go on to rewrite
   * deliveryProviderByMethod and the checkout's delivery method long after the
   * link was abandoned and the address modal put on screen. Aborting makes the
   * request reject instead, so its side-effect block never runs.
   */
  const expired = new AbortController();
  /**
   * Uncovers the storefront once the budget is spent, whatever step is still
   * running.
   *
   * Separate from giving up: the commit that follows the lookup writes the
   * address and cannot be abandoned halfway, and it has no ceiling of its own —
   * two more requests, each retried behind a 30s timeout. Hiding the page until
   * that finished left the blank screen unbounded, which is worse than a moment
   * of the previous warehouse's assortment. The address gate stays suppressed
   * either way; only the covering goes.
   */
  const release = setTimeout(
    () => pickupStoreLinkState.setBlocking(false),
    LINK_BUDGET_MS,
  );
  const stopRelease = () => clearTimeout(release);

  /**
   * Gives up, having first taken back everything the sequence said out loud.
   *
   * Any of these requests can push an alert of its own — the shared request
   * wrapper raises `errors:oops` once it runs out of retries — and none of it
   * belongs on screen in a flow the customer never started and cannot retry.
   * Matched by identity, not by counting: alerts dismiss themselves after five
   * seconds and the budget runs for ten, so a count taken at the start says
   * nothing about what is on screen at the end.
   */
  const abandon = (step: string): false => {
    stopRelease();
    mainStore.dismissAlertsExcept(alertsBefore);

    if (!reportedFailures.has(warehouseCode)) {
      reportedFailures.add(warehouseCode);
      reportWarning(DOMAIN.address, 'Pickup store link not applied', {
        reason: REASON.address.storeLinkFailed,
        extras: { warehouseCode, step },
      });
    }

    return false;
  };

  /** Resolves to null once the step outruns what is left of the budget. */
  const withinBudget = async <T>(work: Promise<T>): Promise<T | null> => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      return await Promise.race([
        work,
        new Promise<null>((resolve) => {
          timer = setTimeout(
            () => {
              expired.abort();
              resolve(null);
            },
            Math.max(0, deadline - Date.now()),
          );
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  };

  /** Whether the step that just failed did so by running out of time. */
  const timedOut = () => expired.signal.aborted;

  const point = await withinBudget(resolvePickupStoreByCode(warehouseCode));

  if (!point) {
    return abandon(timedOut() ? 'resolve_timeout' : 'resolve');
  }

  const pos = `${point.center.lat},${point.center.lng}`;
  // Both read before anything commits: the ETA fetch below can switch the
  // delivery method, and the commit overwrites the very warehouse the cart
  // is compared against.
  const previousMethod = checkoutStore.deliveryMethod;
  const resetCart = isPickupStoreChanging('pickup', point);

  const saved = userStore.isAuthorized
    ? findSavedPickupAddress(point.warehouseCode)
    : undefined;

  // Probe first. A side-effectful ETA for a point no warehouse serves resets
  // DeliveryStore, which drops the delivery provider of the address the
  // customer already had — a broken link would then break their next
  // /calculate rather than politely doing nothing.
  //
  // Run alongside the geocoding, which needs nothing from it: this whole
  // sequence happens with the storefront hidden, so every round-trip that can
  // overlap should. A saved address needs no geocoding at all.
  const probed = await withinBudget(
    Promise.all([
      orderStore
        .probeETA(pos, DeliveryMethods.CLICK_AND_COLLECT, {
          destinationKind: 'customer',
          signal: expired.signal,
        })
        .catch(() => null),
      saved ? Promise.resolve(null) : geocodeStoreAddress(point),
    ]),
  );

  if (!probed) {
    return abandon('probe_timeout');
  }

  const [probe, geocoded] = probed;

  if (!probe) {
    return abandon(timedOut() ? 'probe_timeout' : 'probe');
  }

  const resolved = saved
    ? fromSavedAddress(saved)
    : geocoded && {
        deliveryAddress: geocoded,
        editAddressData: blankEditAddressData(),
      };

  if (!resolved) {
    return abandon('geocode');
  }

  // deliveryProviderByMethod is only refreshed by a side-effectful ETA, and
  // that is what the order payload reads the pickup provider from. The probe
  // above deliberately skipped it, so it has to be fetched once more here —
  // with the same destinationKind, or the two would be answering different
  // questions.
  const etaCalculation = await withinBudget(
    orderStore
      .fetchETA(pos, DeliveryMethods.CLICK_AND_COLLECT, {
        destinationKind: 'customer',
        signal: expired.signal,
      })
      .catch(() => null),
  );

  if (!etaCalculation) {
    return abandon(timedOut() ? 'eta_timeout' : 'eta');
  }

  sendAddressChangedAnalytics(resolved.deliveryAddress, etaCalculation);

  // Past this point the budget no longer applies: the commit writes the
  // address, the warehouse and the cart together, and abandoning halfway
  // through would leave exactly the split state the whole ordering avoids.
  let committed: { savedAddress: DeliveryAddress; persisted: boolean };

  try {
    committed = await commitPickupSelection({
      point,
      deliveryAddress: resolved.deliveryAddress,
      editAddressData: resolved.editAddressData,
      etaCalculation,
      resetCart,
    });
  } catch {
    return abandon('commit');
  }

  // The save helpers answer a rejected write with `persisted: false` rather
  // than throwing. The session is still pointed at the right store — it just
  // lives in local storage — so the link stands and the discrepancy is
  // reported instead.
  if (userStore.isAuthorized && !committed.persisted) {
    reportWarning(DOMAIN.address, 'Pickup store link not persisted', {
      reason: REASON.address.storeLinkNotPersisted,
      extras: { warehouseCode: point.warehouseCode },
    });
  }

  if (checkoutStore.deliveryMethod !== previousMethod) {
    analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECTED, {
      type: 'Pickup',
    });
  }

  sendPostSaveAnalytics(committed.savedAddress);
  stopRelease();

  return true;
}
